import { useMemo } from "react";
import { useAppSelector } from "@store/index";
import { chartDrawerSelector } from "@store/slices/chart-drawer-slice";
import { useGetTabLayoutQuery } from "@store/api/dashboard-api";
import { useGetDatasetByIdQuery } from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type DateGuardrails,
  type GuardrailRollingOption,
  type TimeSeriesViewerOptions,
  type PointInTimeViewerOptions,
} from "@models/Dataset";
import {
  getTabTimeAlignment,
  getTabTimeBreak,
  type TabTimeAlignment,
  type TabTimeFamilyKind,
  type SharedTimeConfig,
} from "@utils/tab-time-controls";

/**
 * Editor-side alignment check: does the visualization being configured have
 * other visualizations in the same family + dataset on the active tab that
 * already agree on one tab-time configuration? Reads tab context from the chart
 * drawer slice (the drawer is mounted outside the router) and the tab layout +
 * dataset guardrails from RTK Query cache.
 */
export function useTabTimeAlignment(
  kind: TabTimeFamilyKind,
  datasetId: string | null,
): TabTimeAlignment {
  const { widgets, guardrailsByDataset, editingWidgetId } =
    useTabTimeContext(datasetId);

  return useMemo(
    () =>
      getTabTimeAlignment({
        widgets,
        kind,
        datasetId,
        currentWidgetId: editingWidgetId,
        guardrailsByDataset,
      }),
    [widgets, kind, datasetId, editingWidgetId, guardrailsByDataset],
  );
}

/**
 * Pre-save break check (Module 4). Returns the shared config the in-progress
 * edit would break (so the editor can offer to restore it), or null when there
 * is no valid alignment to break.
 */
export function useTabTimeBreak(
  kind: TabTimeFamilyKind,
  datasetId: string | null,
  editedSetting: string | null,
  editedRA: GuardrailRollingOption,
  editedViewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions,
): { shared: SharedTimeConfig } | null {
  const { widgets, guardrailsByDataset, editingWidgetId } =
    useTabTimeContext(datasetId);

  return useMemo(
    () =>
      getTabTimeBreak({
        widgets,
        kind,
        datasetId,
        currentWidgetId: editingWidgetId,
        editedSetting,
        editedRA,
        editedViewerOptions,
        guardrailsByDataset,
      }),
    [
      widgets,
      kind,
      datasetId,
      editingWidgetId,
      editedSetting,
      editedRA,
      editedViewerOptions,
      guardrailsByDataset,
    ],
  );
}

/** Shared tab context + guardrails for the editor tab-time hooks. */
function useTabTimeContext(datasetId: string | null) {
  const { dashboardId, tabId, editingWidgetId } =
    useAppSelector(chartDrawerSelector);

  const { data: tabLayout } = useGetTabLayoutQuery(
    { dashboardId: dashboardId ?? "", tabId: tabId ?? "" },
    { skip: !dashboardId || !tabId },
  );

  const { data: dataset } = useGetDatasetByIdQuery(datasetId ?? "", {
    skip: !datasetId,
  });

  const guardrailsByDataset = useMemo(() => {
    const map: Record<string, DateGuardrails> = {};
    if (datasetId && dataset) {
      map[datasetId] = dataset.dateGuardrails ?? buildDefaultDateGuardrails();
    }
    return map;
  }, [datasetId, dataset]);

  return { widgets: tabLayout?.widgets, guardrailsByDataset, editingWidgetId };
}
